import { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Header from "../../layouts/header/Header";
import { useTranslation } from "react-i18next";
import { ProductContext } from "../../context/ProductContext";
import ProductSlider from "../../components/product/ProductSlider";
import Footer from "../footer/Footer";
import Subscription from "../footer/Subscription";
import ProductSliderHorizontal from "../../components/product/RecommendedSlider";
import ProductCard from "../../components/product/ProductCard";
import { Link, useNavigate } from "react-router-dom";
import { useNavigation } from "../../utils/navigation";

import ScrollableGrid from "../../components/section/ScrollableGrid";
import SwiperWrapper from "../../components/section/SwiperWrapper";
import AboutUsSection from "../../components/section/AboutUsSection";
import ContactCardsSection from "../../components/section/ContactCardsSection";
import APIService from "../../services/APIService";

const Container = styled.article`
  color: ${(props) => props.theme.textColor};
  padding-top: 72px;
`;
const Section = styled.section`
  //height: 100vh;
  @media (min-width: 765px) {
    margin-top: 80px;
  }
`;
const Grid = styled.div`
  display: flex;

  gap: 12px;
  cursor: grab;
  @media (min-width: 765px) {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 8px;
  }
  &:active {
    cursor: grabbing; /* Change cursor when actively dragging */
  }
`;
const PromoTitle = styled.h2`
  font-size: var(--font-size-xlarge);

  //margin: 40px 0 14px 0;
`;
const GridCategories = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: flex-start;
  margin: 10px 0;
  @media (min-width: 765px) {
    gap: 14px;
  }
`;
const CategoryItem = styled.div`
  background-color: var(--bg-100);
  padding: 8px 14px;
  font-size: var(--font-size-large);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--bg-300);
  text-transform: uppercase;
  box-sizing: border-box;
  border-radius: 8px;
  flex: 1;
  max-width: 200px;
  &:hover {
    background-color: var(--bg-200);
    border: 1px solid var(--text-200);
    cursor: pointer;
    transition: all 0.4s;
  }
`;
const CustomLink = styled(Link)`
  display: block;
  color: var(--text-color);
  font-size: var(--font-size-base);
  //width: 100%;
  margin: 14px 0;
  @media (min-width: 765px) {
    margin: 0;
  }
`;
const FlexDiv = styled.div`
  width: 100%;

  margin: 14px 0;
  @media (min-width: 765px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
`;
const Br = styled.div`
  height: 80px;
`;
const Disclaimer = styled.div`
  font-size: var(--font-size-large);
  @media (min-width: 765px) {
    font-size: var(--font-size-xxlarge);
  }
  border: 2px solid var(--text-100);
  color: var(--text-100);
  border-radius: 8px;
  text-align: center;
  padding: 14px 0;
  margin: 24px 0;
`;
const GridItem = styled.div`
  @media (max-width: 765px) {
    min-width: 200px;
    width: 200px;
  }
`;

const HomeMain = () => {
  const { t } = useTranslation();
  const { goToCategory, goToBrands, goToNewArrivals } = useNavigation();
  const navigate = useNavigate();
  const {
    products,
    bestSellers,
    newArrivals,
    loadNewArrivals,
    loadProducts,
    loadBestSellers,
    setCategory,
    category,
    categories,
    fetchFeaturedProducts,
  } = useContext(ProductContext);
  // Dynamic state for brands
  const [brandData, setBrandData] = useState({
    velo: [],
    skruf: [],
    cuba: [],
  });

  // List of brands to fetch
  const brands = ["velo", "skruf", "cuba"];

  // Fetch products for all brands
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const updatedBrandData = { ...brandData };
        for (const brand of brands) {
          const response = await APIService.GetProductsByCategory(brand);
          updatedBrandData[brand] = response.products;
        }
        setBrandData(updatedBrandData);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    if (category) {
      loadProducts();
      setCategory(null);
    } else if (products.length) {
      loadBestSellers();
      loadNewArrivals();
    } else {
      loadProducts();
    }
  }, [products]);

  const [openIndices, setOpenIndices] = useState([null, null, null, null]);

  const handleSetOpenIndex = (sectionIndex, index) => {
    const newIndices = [...openIndices];
    newIndices[sectionIndex] =
      newIndices[sectionIndex] === index ? null : index;
    setOpenIndices(newIndices);
  };

  return (
    <>
      <Header />
      <Container>
        <Section>
          <Br />
          <FlexDiv>
            <PromoTitle>{t("HOME.NEW_ARRIVALS")}</PromoTitle>
            <CustomLink to={goToNewArrivals()}>
              {t("BUTTONS.VIEW_ALL")} {t("HOME.NEW_ARRIVALS")}
            </CustomLink>
          </FlexDiv>
          <ScrollableGrid>
            {newArrivals.slice(0, 5).map((product, index) => (
              <GridItem>
                <ProductCard
                  key={index}
                  product={product}
                  isOpen={openIndices[1] === index}
                  setIsOpen={() => handleSetOpenIndex(1, index)}
                />
              </GridItem>
            ))}
          </ScrollableGrid>

          <FlexDiv>
            <PromoTitle style={{ textTransform: "capitalize" }}>
              {brands[0]}
            </PromoTitle>
            <CustomLink to={goToCategory(brands[0])}>
              {t("BUTTONS.VIEW_ALL")}
            </CustomLink>
          </FlexDiv>
          <ScrollableGrid>
            {brandData[brands[0]]?.slice(0, 5).map((product, index) => (
              <GridItem>
                <ProductCard
                  key={index}
                  product={product}
                  isOpen={openIndices[2] === index}
                  setIsOpen={() => handleSetOpenIndex(2, index)}
                />
              </GridItem>
            ))}
          </ScrollableGrid>
          <Br />
          <FlexDiv>
            <PromoTitle>{t("HOME.BRANDS")}</PromoTitle>
            <CustomLink to={goToBrands()}>
              {t("BUTTONS.VIEW_ALL")} {t("HOME.BRANDS")}
            </CustomLink>
          </FlexDiv>

          <GridCategories>
            {categories.slice(0, 11).map((category, index) => (
              <CategoryItem
                key={index}
                onClick={() => navigate(goToCategory(category.name))}
              >
                {category.name}
              </CategoryItem>
            ))}
          </GridCategories>
          <Disclaimer>{t("HOME.DISCLAIMER")}</Disclaimer>

          <FlexDiv>
            <PromoTitle style={{ textTransform: "capitalize" }}>
              {brands[1]}
            </PromoTitle>
            <CustomLink to={goToCategory(brands[1])}>
              {t("BUTTONS.VIEW_ALL")}
            </CustomLink>
          </FlexDiv>

          <ScrollableGrid>
            {brandData[brands[1]]?.slice(0, 5).map((product, index) => (
              <GridItem>
                <ProductCard key={index} product={product} />
              </GridItem>
            ))}
          </ScrollableGrid>

          <FlexDiv>
            <PromoTitle style={{ textTransform: "capitalize" }}>
              {brands[2]}
            </PromoTitle>
            <CustomLink to={goToCategory(brands[0])}>
              {t("BUTTONS.VIEW_ALL")}
            </CustomLink>
          </FlexDiv>
          <ScrollableGrid>
            {brandData[brands[2]]?.slice(0, 5).map((product, index) => (
              <GridItem>
                <ProductCard key={index} product={product} />
              </GridItem>
            ))}
          </ScrollableGrid>

          <SwiperWrapper items={categories.slice(0, 3)} />

          <ContactCardsSection />

          <AboutUsSection />
        </Section>
      </Container>

      <Subscription />
      <Footer />
    </>
  );
};

export default HomeMain;
